import { clsx } from '@alexis/helpers/clsx';
import { toISOStringWithTimezone } from '@alexis/helpers/date';
import { genzoTrackActionEvent, genzoTrackPageView } from '@alexis/helpers/genzo';
import { generateCUID } from '@alexis/helpers/identity';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import { TEST_FEATURES_PARAM } from '@constants/flight';

import { GlobalContext } from '@context/GlobalContext';

import { deleteFilterStatesFromSearchParam } from '@helpers/flight';
import { cloudinaryImageTransformations } from '@helpers/imageTransformations';
import { flightsRoute, hotelsRoute } from '@helpers/routeTranslation';

import { useIsAuthNew } from '@hooks/useIsAuthNew';

import {
  getApiBaseUrlState,
  getCurrentSiteState,
  getLocaleState,
  getTranslationsState,
  getUserState,
} from '@redux/selectors';

import styles from './RefreshPopup.module.scss';

export enum ProductTypes {
  FLIGHTS = 'flights',
  HOTELS = 'hotels',
}

interface RefreshPopupProps {
  genzoTrackPageViewBaseType: string;
  product?: ProductTypes;
  localeParam: string | undefined;
  onRefresh: () => void;
  onClose?: () => void;
  wegoAnalyticsClientId: string | undefined;
  wegoAnalyticsClientSessionId: string | undefined;
}

const RefreshPopup: React.FC<RefreshPopupProps> = ({
  product = ProductTypes.FLIGHTS,
  genzoTrackPageViewBaseType,
  localeParam,
  onRefresh,
  onClose,
  wegoAnalyticsClientId,
  wegoAnalyticsClientSessionId,
}) => {
  const apiBaseUrl = useSelector(getApiBaseUrlState);
  const currentSite = useSelector(getCurrentSiteState);
  const locale = useSelector(getLocaleState);
  const translations = useSelector(getTranslationsState);
  const user = useSelector(getUserState);

  const { webEngageAnonymousId } = useContext(GlobalContext);

  const { pathname, search, state } = useLocation();
  const navigate = useNavigate();
  const { isAuthNew, login } = useIsAuthNew();

  const searchWithoutExtraParams = useMemo<string>(() => {
    const searchParams = new URLSearchParams(search);

    searchParams.delete('modal');
    searchParams.delete('search_id');
    searchParams.delete('guests');
    searchParams.delete('sort');
    searchParams.delete('order');
    searchParams.delete('name');
    searchParams.delete('deals');
    searchParams.delete('amenities');
    searchParams.delete('providers');
    searchParams.delete('brands');
    searchParams.delete('chains');
    searchParams.delete('districts');
    searchParams.delete('price');
    searchParams.delete('accommodation_types');
    searchParams.delete('free_amenities');
    searchParams.delete('guest_rating');
    searchParams.delete('guest_score');
    searchParams.delete('stars');
    searchParams.delete('provider_codes');
    searchParams.delete('open_calendar');
    searchParams.delete('outbound_date');
    searchParams.delete('inbound_date');
    searchParams.delete('dep_code');
    searchParams.delete('arr_code');
    searchParams.delete('trip_type');
    searchParams.delete('reference_id');
    searchParams.delete('sponsored_ad_id');
    searchParams.delete('booking_id');
    searchParams.delete('booking_page_view_id');
    searchParams.delete('selected_hotel_ids');
    searchParams.delete('payment_methods');
    searchParams.delete('view');
    searchParams.delete(TEST_FEATURES_PARAM);
    searchParams.delete('session_id');
    searchParams.delete('selected_trip_id');

    //hotel params
    searchParams.delete('provider');
    searchParams.delete('wego_hotel_id');

    const updatedSearchParams = deleteFilterStatesFromSearchParam(searchParams).toString();

    return !!updatedSearchParams ? `?${updatedSearchParams}` : '';
  }, [search, deleteFilterStatesFromSearchParam]);

  const pageViewId = useMemo<string>(() => {
    return generateCUID();
  }, []);

  const [isPageViewed, setIsPageViewed] = useState<boolean>(document.visibilityState === 'visible');

  const handleVisibilityChange = useCallback(() => {
    setIsPageViewed(document.visibilityState === 'visible');
  }, []);

  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleVisibilityChange]);

  // Genzo Tracking
  useEffect(() => {
    if (
      !!wegoAnalyticsClientId &&
      !!wegoAnalyticsClientSessionId &&
      !!webEngageAnonymousId &&
      isPageViewed
    ) {
      const clientData: any = {
        id: wegoAnalyticsClientId,
        session_id: wegoAnalyticsClientSessionId,
      };

      if (user) {
        clientData.user_hash = user.userHash;
      }

      const pageData: any = {
        name:
          product === ProductTypes.FLIGHTS
            ? 'Flights Results | Refresh Prompt'
            : 'Hotels Results | Refresh Prompt',
        product: product,
        base_type: genzoTrackPageViewBaseType,
        sub_type: 'refresh_results',
        locale,
        site_code: currentSite.countryCode,
        url: window.location.href,
        referrer_url: document.referrer,
      };

      const data = {
        id: pageViewId,
        client: clientData,
        page: pageData,
        created_at: toISOStringWithTimezone(new Date()),
        external_services: {
          crm_id: user ? user.userHash : webEngageAnonymousId,
        },
      };

      genzoTrackPageView(apiBaseUrl, data);
    }
  }, [
    apiBaseUrl,
    genzoTrackPageViewBaseType,
    wegoAnalyticsClientId,
    wegoAnalyticsClientSessionId,
    currentSite.countryCode,
    locale,
    pageViewId,
    user,
    webEngageAnonymousId,
    isPageViewed,
  ]);

  const goBackToHomePageClicked = () => {
    if (!!wegoAnalyticsClientId && !!wegoAnalyticsClientSessionId) {
      const eventData = {
        id: pageViewId,
        category: 'refresh_results',
        object: 'refresh_results',
        action: 'back_to_homepage',
        value: 'back_to_homepage',
      };
      genzoTrackActionEvent(
        apiBaseUrl,
        wegoAnalyticsClientId,
        wegoAnalyticsClientSessionId,
        user?.userHash,
        eventData,
      );
    }
  };

  const refreshButtonClicked = () => {
    if (!!wegoAnalyticsClientId && !!wegoAnalyticsClientSessionId) {
      const eventData = {
        id: pageViewId,
        category: 'refresh_results',
        object: 'refresh_results',
        action: 'refresh_search',
        value: 'refresh_search',
      };
      genzoTrackActionEvent(
        apiBaseUrl,
        wegoAnalyticsClientId,
        wegoAnalyticsClientSessionId,
        user?.userHash,
        eventData,
      );
    }
    onRefresh();
  };

  const loginButtonClicked = () => {
    if (!!wegoAnalyticsClientId && !!wegoAnalyticsClientSessionId) {
      const eventData = {
        id: pageViewId,
        category: 'refresh_results',
        object: 'refresh_results',
        action: 'login_to_unlock_deals',
        value: 'login_to_unlock_deals',
      };
      genzoTrackActionEvent(
        apiBaseUrl,
        wegoAnalyticsClientId,
        wegoAnalyticsClientSessionId,
        user?.userHash,
        eventData,
      );
    }
  };

  const handleLoginButtonClick = async () => {
    await loginButtonClicked();

    if (isAuthNew) {
      login();
      return;
    }

    const searchParams = new URLSearchParams(search);
    searchParams.delete('modal');
    searchParams.append('modal', 'login');

    navigate({ pathname, search: searchParams.toString() }, { replace: true, state });
    onClose && onClose();
  };

  return (
    <div>
      <div
        data-testid='search-expired-popup'
        className={styles.overlay}
        onClick={refreshButtonClicked}
      ></div>
      <div className={styles.dialog}>
        <img
          src={cloudinaryImageTransformations(
            'https://assets.wego.com/image/upload/v1705998176/web/misc/finding-flight.png',
            'c_fill',
            'f_auto',
            'q_auto:low',
            'fl_lossy',
          )}
        />

        <div className={styles.title}>
          {!!user || product === ProductTypes.FLIGHTS
            ? translations.search_expired_title
            : translations.search_expired_title_non_user}
        </div>

        {!!user || product === ProductTypes.FLIGHTS ? (
          <div className={styles.subTitle}>{translations.search_expired_subtitle}</div>
        ) : (
          <>
            <div className={styles.subTitle}>{translations.search_expired_subtitle_non_user_1}</div>
            <div className={styles.subTitle}>{translations.search_expired_subtitle_non_user_2}</div>
          </>
        )}

        <div className={styles.buttonsContainer}>
          {!!user || product === ProductTypes.FLIGHTS ? (
            <NavLink
              onClick={goBackToHomePageClicked}
              state={state}
              to={{
                pathname:
                  product === ProductTypes.FLIGHTS
                    ? flightsRoute(currentSite, localeParam)
                    : hotelsRoute(currentSite, localeParam),
                search: searchWithoutExtraParams,
              }}
            >
              <button className={clsx(styles.goBackButton, styles.button)}>
                {translations.go_back_to_homepage}
              </button>
            </NavLink>
          ) : (
            <button
              onClick={handleLoginButtonClick}
              className={clsx(styles.logInButton, styles.button)}
            >
              {translations.login_to_unlock_deals}
            </button>
          )}

          <button
            className={clsx(styles.refreshButton, styles.button)}
            onClick={refreshButtonClicked}
          >
            {translations.refresh_search}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RefreshPopup;
